import { INSURANCE_COVER, type PricingInsuranceCoverOption } from "@constants/insuranceCover";
import { getIndianCurrencyFormatForMaskedPrice } from "@helpers/getIndianCurrencyFormat";
import { formatCurrencyINR } from "@utils/numberFormatter";

interface PricingComponents {
  [key: string]: number | string;
}

type SectionName = "primary" | "insurance" | "misc" | "savings" | "summary";

export interface PriceBreakupRow {
  label: string;
  value: string | null;
  section?: SectionName;
}

export interface AdditionalAttributes {
  kind_offers: string;
  waiting_period?: string;
  delivery_days?: string;
}
export interface DiscountComponents {
  ackodiscount: string;
  newyear_discount: string;
  consumerdiscount: string;
  special_discount: string;
  ripdiscount: string;
}
export interface PriceBreakup {
  id: string;
  ex_showroom_price: number;
  pricing_components: PricingComponents;
  onroad_discount_components: any;
  discount_components: PricingComponents;
  additional_attributes: AdditionalAttributes;
  booking_components: Record<string, unknown>;
  market_price: string;
  onroad_price: string;
  acko_drive_price: string;
  acko_drive_savings: string;
  insurance_covers?: PricingInsuranceCoverOption[];
}

export interface V2PricingBucket extends Omit<PriceBreakup, "market_price" | "onroad_price" | "acko_drive_price" | "acko_drive_savings"> {
  market_price: string | number;
  onroad_price: string | number;
  acko_drive_price: string | number;
  acko_drive_savings: string | number;
}

export interface V2ColorPricingData {
  expressPrice?: V2PricingBucket | null;
  adspPrice?: V2PricingBucket | null;
}

export interface V2PriceBreakupResponse {
  default_color?: string;
  response?: Record<string, V2ColorPricingData>;
}

export interface NormalizedCustomCardPriceBreakup {
  charges: PriceBreakupRow[];
  savingsRows: PriceBreakupRow[];
  summaryRows: PriceBreakupRow[];
  finalPrice: string;
  isExpressDelivery: boolean;
  deliveryDate: string;
}

interface PriceData {
  pricing_components?: PricingComponents;
  discount_components?: PricingComponents;
  ex_showroom_price?: number | string;
  market_price?: number | string;
  onroad_price?: number | string;
  acko_drive_price?: number | string;
  acko_drive_savings?: number | string;
}

const componentLabels: Record<string, string> = {
  ex_showroom: "Ex-showroom price",
  "Ex-showroom": "Ex-showroom price",
  registration_amount: "Registration amount",
  "Registration amount": "Registration amount",
  "Registration Amount": "Registration amount",
  dealer_insurance_premium: "Dealer insurance premium",
  "Dealer insurance premium": "Dealer insurance premium",
  "Dealer Insurance Premium": "Dealer insurance premium",
  tax_collected_at_source: "Tax collected at source",
  "Tax collected at source": "Tax collected at source",
  hypothecation_charges: "Hypothecation",
  "Hypothecation charges": "Hypothecation",
  fast_tag: "FASTag",
  Fastag: "FASTag",
  "HSRP number plate charges": "HSRP number plate charges",
  mcd_charges: "MCD charge",
  "MCD charge": "MCD charge",
  auto_card: "Auto card",
  "Auto card": "Auto card",
  basic_accessories: "Basic accessories",
  "Basic accessories": "Basic accessories",
};

const REGISTRATION_COMPONENT_KEYS = [
  "registration_amount",
  "Registration amount",
  "Registration Amount",
] as const;

const DEALER_INSURANCE_COMPONENT_KEYS = [
  "dealer_insurance_premium",
  "Dealer insurance premium",
  "Dealer Insurance Premium",
] as const;

const EX_SHOWROOM_COMPONENT_KEYS = [
  "ex_showroom",
  "Ex-showroom",
] as const;

const STANDARD_INSURANCE_LABEL =
  "Dealer Insurance - Standard (1 + 3)";

const getValue = (breakUp: any, value: string): string | null =>
  breakUp[value] ? formatCurrencyINR(breakUp[value]) : null;

const getNumericValue = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getComponentValue = (
  components: PricingComponents = {},
  keys: readonly string[]
): number => {
  const matchingKey = Object.keys(components).find((componentKey) =>
    keys.some((key) => key.toLowerCase() === componentKey.toLowerCase())
  );

  return getNumericValue(matchingKey ? components[matchingKey] : 0);
};

const getDiscountValue = (
  components: PricingComponents = {},
  keys: readonly string[]
): number => {
  const matchingKey = Object.keys(components).find((componentKey) =>
    keys.some((key) => key.toLowerCase() === componentKey.toLowerCase())
  );

  return getNumericValue(matchingKey ? components[matchingKey] : 0);
};

const sortRowsByAmountDesc = <T extends { value: string | null }>(
  rows: T[]
): T[] => {
  const getAmountFromFormattedValue = (value: string | null) =>
    getNumericValue(String(value || "").replace(/[^\d.-]/g, ""));

  return [...rows].sort(
    (firstRow, secondRow) =>
      getAmountFromFormattedValue(secondRow.value) -
      getAmountFromFormattedValue(firstRow.value)
  );
};

export const chargesCalculationV1 = (priceData: PriceData | any): { label: string; value: string | null }[] => {
  if (priceData?.price?.price_breakup) {
    const priceBreakup = priceData.price.price_breakup || priceData.pricing_components;
    return [
      {
        label: "Ex-showroom price",
        value: getValue(priceBreakup, "ex_showroom"),
      },
      {
        label: "Registration amount",
        value: getValue(priceBreakup, "registration_amount"),
      },
      {
        label: "Dealer insurance premium",
        value: getValue(priceBreakup, "dealer_insurance_premium"),
      },
      {
        label: "Tax",
        value: getValue(priceBreakup, "tax_collected_at_source"),
      },
      {
        label: "Hypothecation",
        value: getValue(priceBreakup, "hypothecation_charges"),
      },
      {
        label: "Mcd Charges",
        value: getValue(priceBreakup, "mcd_charges"),
      },
      {
        label: "Auto Card",
        value: getValue(priceBreakup, "auto_card"),
      },
      {
        label: "FASTag",
        value: getValue(priceBreakup, "fast_tag"),
      },
      {
        label: "Basic Accessories",
        value: getValue(priceBreakup, "basic_accessories"),
      },
    ];
  }

  if (!priceData?.pricing_components) {
    return [];
  }

  const pricingComponents = priceData.pricing_components;
  const charges: { label: string; value: string | null }[] = [];

  const exShowroomPrice =
    priceData.ex_showroom_price ||
    pricingComponents.ex_showroom ||
    pricingComponents["Ex-showroom"];

  if (exShowroomPrice) {
    charges.push({
      label: "Ex-showroom price",
      value: formatCurrencyINR(exShowroomPrice),
    });
  }

  Object.entries(pricingComponents).forEach(([key, value]) => {
    if (
      (key === "ex_showroom" || key === "Ex-showroom") &&
      exShowroomPrice
    ) {
      return;
    }

    if (value === null || value === undefined) {
      return;
    }
    const numValue = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(numValue) || numValue === 0) {
      return;
    }

    const label = componentLabels[key] || key;
    const formattedValue = formatCurrencyINR(numValue);
    if (formattedValue && formattedValue !== "₹0") {
      charges.push({
        label,
        value: formattedValue,
      });
    }
  });

  return charges;
};

export const chargesCalculationV2 = (priceData: PriceBreakup): { label: string; value: string | null }[] => {
  const pricingComponents = priceData.pricing_components;
  const charges: { label: string; value: string | null }[] = [];

  Object.keys(componentLabels).forEach((key) => {
    if (!(key in pricingComponents)) {
      return;
    }

    const value = pricingComponents[key as keyof typeof pricingComponents];

    if (value === null || value === undefined) {
      return;
    }

    const label = componentLabels[key];
    const formattedValue = formatCurrencyINR(Number(value));
    if (formattedValue && formattedValue !== "₹0") {
      charges.push({
        label,
        value: formattedValue,
      });
    }
  });

  return charges;
};

const getDefaultColorData = (
  priceBreakupData?: V2PriceBreakupResponse | null
): V2ColorPricingData | null => {
  const response = priceBreakupData?.response;
  if (!response || Object.keys(response).length === 0) {
    return null;
  }

  const defaultColor =
    priceBreakupData?.default_color || Object.keys(response)[0];

  return response[defaultColor] || response[Object.keys(response)[0]] || null;
};

const getSelectedPricingBucket = ({
  priceBreakupData,
  isLoggedIn,
}: {
  priceBreakupData?: V2PriceBreakupResponse | null;
  isLoggedIn: boolean;
}): {
  priceData: V2PricingBucket | null;
  isExpressDelivery: boolean;
} => {
  const colorData = getDefaultColorData(priceBreakupData);

  if (!colorData) {
    return {
      priceData: null,
      isExpressDelivery: false,
    };
  }

  if (isLoggedIn && colorData.expressPrice) {
    return {
      priceData: colorData.expressPrice,
      isExpressDelivery: true,
    };
  }

  return {
    priceData: colorData.adspPrice || colorData.expressPrice || null,
    isExpressDelivery: !colorData.adspPrice && Boolean(colorData.expressPrice),
  };
};

const getPriceDataForStandardInsurance = (
  priceData: V2PricingBucket
): V2PricingBucket => {
  const standardCover = priceData.insurance_covers?.find(
    (cover) => cover.cover_type === INSURANCE_COVER.STANDARD
  );

  if (!standardCover) {
    return priceData;
  }

  return {
    ...priceData,
    pricing_components:
      standardCover.pricing_components || priceData.pricing_components,
    discount_components:
      standardCover.discount_components || priceData.discount_components,
    market_price:
      standardCover.market_price ?? standardCover.on_road_price ?? priceData.market_price,
    onroad_price:
      standardCover.on_road_price ?? standardCover.market_price ?? priceData.onroad_price,
    acko_drive_price:
      standardCover.acko_drive_price ?? priceData.acko_drive_price,
    acko_drive_savings:
      standardCover.acko_drive_savings ?? priceData.acko_drive_savings,
  };
};

export const normalizeCustomCardPriceBreakup = ({
  priceBreakupData,
  cityName,
  isLoggedIn,
}: {
  priceBreakupData?: V2PriceBreakupResponse | null;
  cityName: string;
  isLoggedIn: boolean;
}): NormalizedCustomCardPriceBreakup | null => {
  const { priceData, isExpressDelivery } = getSelectedPricingBucket({
    priceBreakupData,
    isLoggedIn,
  });

  if (!priceData) {
    return null;
  }

  const selectedPriceData = getPriceDataForStandardInsurance(priceData);
  const pricingComponents = selectedPriceData.pricing_components || {};
  const discountComponents = selectedPriceData.discount_components || {};

  const exShowroomPrice = getNumericValue(
    selectedPriceData.ex_showroom_price ||
      getComponentValue(pricingComponents, EX_SHOWROOM_COMPONENT_KEYS)
  );
  const registrationAmount = getComponentValue(
    pricingComponents,
    REGISTRATION_COMPONENT_KEYS
  );
  const dealerInsurancePremium = getComponentValue(
    pricingComponents,
    DEALER_INSURANCE_COMPONENT_KEYS
  );
  const consumerSavings = getDiscountValue(discountComponents, [
    "consumerdiscount",
    "Consumer Discount",
  ]);
  const ackoDriveSavings = getNumericValue(selectedPriceData.acko_drive_savings);
  const onRoadPrice = getNumericValue(
    selectedPriceData.onroad_price || selectedPriceData.market_price
  );
  const finalPriceValue = getNumericValue(
    selectedPriceData.acko_drive_price ||
      selectedPriceData.onroad_price ||
      selectedPriceData.market_price
  );

  const charges: PriceBreakupRow[] = [];

  if (exShowroomPrice > 0) {
    charges.push({
      label: "Ex-showroom price",
      value: formatCurrencyINR(exShowroomPrice),
      section: "primary",
    });
  }

  if (registrationAmount > 0) {
    charges.push({
      label: "Registration amount",
      value: formatCurrencyINR(registrationAmount),
      section: "primary",
    });
  }

  if (dealerInsurancePremium > 0) {
    charges.push({
      label: STANDARD_INSURANCE_LABEL,
      value: formatCurrencyINR(dealerInsurancePremium),
      section: "insurance",
    });
  }

  Object.entries(pricingComponents).forEach(([key, value]) => {
    const normalizedKey = key.toLowerCase();
    const isDuplicateKey =
      EX_SHOWROOM_COMPONENT_KEYS.some((componentKey) => componentKey.toLowerCase() === normalizedKey) ||
      REGISTRATION_COMPONENT_KEYS.some((componentKey) => componentKey.toLowerCase() === normalizedKey) ||
      DEALER_INSURANCE_COMPONENT_KEYS.some((componentKey) => componentKey.toLowerCase() === normalizedKey);

    if (isDuplicateKey) {
      return;
    }

    const numericValue = getNumericValue(value);

    if (numericValue <= 0) {
      return;
    }

    charges.push({
      label: componentLabels[key] || key,
      value: formatCurrencyINR(numericValue),
      section: "misc",
    });
  });

  const savingsRows: PriceBreakupRow[] = [];

  if (consumerSavings > 0) {
    savingsRows.push({
      label: "Consumer Savings",
      value: formatCurrencyINR(consumerSavings),
      section: "savings",
    });
  }

  if (ackoDriveSavings > 0 || !isLoggedIn) {
    savingsRows.push({
      label: "ACKO Drive discount",
      value: isLoggedIn
        ? `-${formatCurrencyINR(ackoDriveSavings)}`
        : `-₹${getIndianCurrencyFormatForMaskedPrice(selectedPriceData.acko_drive_savings)}`,
      section: "summary",
    });
  }

  const summaryRows: PriceBreakupRow[] = [
    {
      label: cityName ? `On-road price in ${cityName}` : "On-road price",
      value: formatCurrencyINR(onRoadPrice),
      section: "summary",
    },
  ];

  const chargesBySection = charges.reduce<Record<SectionName, PriceBreakupRow[]>>(
    (sections, charge) => {
      const section = charge.section || "misc";
      sections[section].push(charge);
      return sections;
    },
    {
      primary: [],
      insurance: [],
      misc: [],
      savings: [],
      summary: [],
    }
  );

  return {
    charges: [
      ...sortRowsByAmountDesc(chargesBySection.primary),
      ...sortRowsByAmountDesc(chargesBySection.insurance),
      ...sortRowsByAmountDesc(chargesBySection.misc),
    ],
    savingsRows,
    summaryRows,
    finalPrice: isLoggedIn
      ? formatCurrencyINR(finalPriceValue)
      : `₹${getIndianCurrencyFormatForMaskedPrice(selectedPriceData.acko_drive_price || selectedPriceData.onroad_price || selectedPriceData.market_price)}`,
    isExpressDelivery,
    deliveryDate:
      selectedPriceData.additional_attributes?.delivery_days ||
      selectedPriceData.additional_attributes?.waiting_period ||
      "",
  };
};
